/**
 * Real User Monitoring van Core Web Vitals.
 *
 * Draait uitsluitend in de browser (client-only effect, geen module-level
 * neveneffect) en verstuurt pas bij het verlaten/verbergen van de pagina —
 * dan pas hebben CLS en INP hun definitieve waarde.
 *
 * Omdat TanStack Router client-side navigeert, wordt per metriek bijgehouden
 * op welke route de meting is ontstaan. CLS wordt per route opgeteld uit de
 * deltas, INP per route als hoogste waargenomen waarde.
 *
 * Privacy: geen cookie, geen IP, geen identificatie die de paginaweergave
 * overleeft. De `page_load_id` is een willekeurige waarde per paginaweergave.
 */
import { useEffect, useRef } from "react";
import { useRouterState } from "@tanstack/react-router";

import {
  isLikelyBot,
  normalizeRoute,
  type DeviceType,
  type MetricName,
  type VitalsBeacon,
  type VitalsBeaconMetric,
} from "@/lib/web-vitals";

const ENDPOINT = "/api/public/vitals";

type PerRoute = { value: number; navigationType: string; attribution: Record<string, unknown> };

function detectDevice(): DeviceType {
  const coarse =
    typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
  const narrow = window.innerWidth <= 820;
  return coarse && narrow ? "mobile" : narrow ? "mobile" : "desktop";
}

function detectConnection(): string | null {
  const nav = navigator as Navigator & { connection?: { effectiveType?: string } };
  const type = nav.connection?.effectiveType;
  return typeof type === "string" && type.length <= 20 ? type : null;
}

/** Alleen de bruikbare velden uit de attributiedata; nooit hele DOM-nodes. */
function pickAttribution(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== "object") return {};
  const src = raw as Record<string, unknown>;
  const keys = [
    "element",
    "target",
    "url",
    "largestShiftTarget",
    "interactionTarget",
    "interactionType",
    "loadState",
    "timeToFirstByte",
    "resourceLoadDelay",
    "resourceLoadDuration",
    "elementRenderDelay",
    "inputDelay",
    "processingDuration",
    "presentationDelay",
    "largestShiftValue",
    "waitingDuration",
    "cacheDuration",
    "dnsDuration",
    "connectionDuration",
    "requestDuration",
  ];
  const out: Record<string, unknown> = {};
  for (const key of keys) {
    const value = src[key];
    if (typeof value === "string") out[key] = value.slice(0, 300);
    else if (typeof value === "number" && Number.isFinite(value)) out[key] = Math.round(value * 1000) / 1000;
  }
  return out;
}

export function WebVitalsReporter() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const routeRef = useRef<string>("/");

  // Route bijhouden zonder de meet-effect opnieuw op te starten.
  useEffect(() => {
    routeRef.current = normalizeRoute(pathname);
  }, [pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isLikelyBot(navigator.userAgent)) return;

    let cancelled = false;
    // De databank verwacht een uuid; bij oudere browsers zelf er een bouwen.
    const pageLoadId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
          });

    // Per metriek per route: één samengevoegde meting.
    const buckets = new Map<MetricName, Map<string, PerRoute>>();
    let sent = false;

    const record = (
      name: MetricName,
      route: string,
      value: number,
      navigationType: string,
      attribution: Record<string, unknown>,
      mode: "sum" | "max" | "last",
    ) => {
      let byRoute = buckets.get(name);
      if (!byRoute) {
        byRoute = new Map();
        buckets.set(name, byRoute);
      }
      const existing = byRoute.get(route);
      if (!existing) {
        byRoute.set(route, { value, navigationType, attribution });
        return;
      }
      if (mode === "sum") existing.value += value;
      else if (mode === "max") existing.value = Math.max(existing.value, value);
      else existing.value = value;
      existing.attribution = attribution;
    };

    const collect = (): VitalsBeaconMetric[] => {
      const out: VitalsBeaconMetric[] = [];
      for (const [name, byRoute] of buckets) {
        for (const [route, entry] of byRoute) {
          if (!Number.isFinite(entry.value)) continue;
          out.push({
            name,
            value: Math.round(entry.value * 10000) / 10000,
            route,
            navigationType: entry.navigationType,
            attribution: entry.attribution,
          });
        }
      }
      return out;
    };

    const flush = () => {
      if (sent || cancelled) return;
      const metrics = collect();
      if (!metrics.length) return;
      sent = true;

      const payload: VitalsBeacon = {
        pageLoadId,
        deviceType: detectDevice(),
        connectionType: detectConnection(),
        metrics,
      };

      if (!import.meta.env.PROD) {
        // In ontwikkeling nooit naar de echte tabel schrijven.
        console.info("[web-vitals] (dev, niet verstuurd)", payload);
        return;
      }

      const body = JSON.stringify(payload);
      try {
        const blob = new Blob([body], { type: "application/json" });
        if (!navigator.sendBeacon?.(ENDPOINT, blob)) {
          void fetch(ENDPOINT, { method: "POST", body, keepalive: true, headers: { "Content-Type": "application/json" } });
        }
      } catch {
        /* meting mag nooit de pagina breken */
      }
    };

    void import("web-vitals/attribution").then((wv) => {
      if (cancelled) return;

      wv.onLCP((m) => record("LCP", routeRef.current, m.value, m.navigationType, pickAttribution(m.attribution), "last"));
      wv.onFCP((m) => record("FCP", routeRef.current, m.value, m.navigationType, pickAttribution(m.attribution), "last"));
      wv.onTTFB((m) => record("TTFB", routeRef.current, m.value, m.navigationType, pickAttribution(m.attribution), "last"));

      // CLS: per route de som van de deltas, zodat een layout shift niet aan
      // een volgende route wordt toegeschreven.
      wv.onCLS(
        (m) => record("CLS", routeRef.current, m.delta, m.navigationType, pickAttribution(m.attribution), "sum"),
        { reportAllChanges: true },
      );

      // INP: per route de traagste interactie die op die route plaatsvond.
      wv.onINP(
        (m) => record("INP", routeRef.current, m.value, m.navigationType, pickAttribution(m.attribution), "max"),
        { reportAllChanges: true },
      );
    });

    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flush);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flush);
    };
  }, []);

  return null;
}
